import { fromJS, List, Record } from 'immutable';
import {
  generateKeys,
  generateSchema,
  setInModel,
} from '../../../utils/helpers/model.helper';
import { StatementAging } from './statement-aging.model';
import { StatementDocument } from './statement-document.model';
import { StatementLabels } from './statement-labels.model';
import { StatementSummary } from './statement-summary.model';
import { Document } from '../../document-data/document.model';

// statement response from backend
const STATEMENT_SCHEMA = Object.freeze({
  STATEMENT_DATE: '', // i2g-timezoneless-date (yyyy-mm-dd)
  CLIENT_ID: '',
  START_DATE: '', // i2g-timezoneless-date (yyyy-mm-dd)
  END_DATE: '', // i2g-timezoneless-date (yyyy-mm-dd)
  UNPAID_ONLY: false,
  CURRENCY_CODE: '',
  LOCALE: '',
  OPENING_BALANCE: 0,
  FUTURE_BALANCE: 0,
  AMOUNT_DUE: 0,
  CARD_PAYMENTS_ENABLED: false,
  PAYPAL_EC_ENABLED: false,
  HAS_MISMATCH_PAYMENT_METHODS: false,
  SUMMARY: StatementSummary,
  DOCUMENTS: List<StatementDocument>([]),
  DOCUMENT_SNIPPETS: List<Document>([]),
  AGING: StatementAging,
  LABELS: StatementLabels,
});
const RECORD = generateSchema(STATEMENT_SCHEMA);
const KEYS: any = generateKeys(STATEMENT_SCHEMA);

export class StatementContent extends Record(RECORD) {
  static KEYS: any = KEYS;

  constructor(props?: any) {
    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    super(props);

    return setInModel(this, [
      {
        key: KEYS.SUMMARY,
        value: new StatementSummary(this.summary),
      },
      {
        key: KEYS.DOCUMENTS,
        value: props.get(KEYS.DOCUMENTS)
          ? this.documents.map((params) => new StatementDocument(params))
          : List<StatementDocument>([]),
      },
      {
        key: KEYS.DOCUMENT_SNIPPETS,
        value: props.get(KEYS.DOCUMENT_SNIPPETS)
          ? this.documentSnippets.map((params) => new Document(params))
          : List<Document>([]),
      },
      {
        key: KEYS.AGING,
        value: new StatementAging(this.aging),
      },
      {
        key: KEYS.LABELS,
        value: new StatementLabels(this.labels),
      },
    ]) as StatementContent;
  }

  get statementDate(): string {
    return this.get(KEYS.STATEMENT_DATE);
  }

  get clientId(): string {
    return this.get(KEYS.CLIENT_ID);
  }

  get startDate(): string {
    return this.get(KEYS.START_DATE);
  }

  get endDate(): string {
    return this.get(KEYS.END_DATE);
  }

  get unpaidOnly(): boolean {
    return this.get(KEYS.UNPAID_ONLY);
  }

  get currencyCode(): string {
    return this.get(KEYS.CURRENCY_CODE);
  }

  get locale(): string {
    return this.get(KEYS.LOCALE);
  }

  get openingBalance(): number {
    return this.get(KEYS.OPENING_BALANCE);
  }

  get futureBalance(): number {
    return this.get(KEYS.FUTURE_BALANCE);
  }

  get amountDue(): number {
    return this.get(KEYS.AMOUNT_DUE);
  }

  get cardPaymentsEnabled(): boolean {
    return this.get(KEYS.CARD_PAYMENTS_ENABLED);
  }

  get paypalEcEnabled(): boolean {
    return this.get(KEYS.PAYPAL_EC_ENABLED);
  }

  get hasMismatchPaymentMethods(): boolean {
    return this.get(KEYS.HAS_MISMATCH_PAYMENT_METHODS);
  }

  get summary(): StatementSummary {
    return this.get(KEYS.SUMMARY);
  }

  get documents(): List<StatementDocument> {
    return this.get(KEYS.DOCUMENTS);
  }

  get documentSnippets(): List<Document> {
    return this.get(KEYS.DOCUMENT_SNIPPETS);
  }

  get aging(): StatementAging {
    return this.get(KEYS.AGING);
  }

  get labels(): StatementLabels {
    return this.get(KEYS.LABELS);
  }
}
